/**
 * WCAG kontrast yardımcıları.
 *
 * NEDEN GEREKLİ: Referans arayüzün ana rengi #8FBD56, beyaz üzerinde yalnızca
 * ~1,9:1 kontrast verir. WCAG AA metin eşiği 4,5:1'dir. Yani bu yeşil dolgu,
 * grafik ve ikon dairesi olarak güzel çalışır ama LİNK veya METİN rengi olarak
 * okunmaz.
 *
 * Çözüm: her ana renk için otomatik türetilen, AA'yı geçen bir "güçlü" varyant.
 * Tema seçicideki özel renk seçici de bu fonksiyondan geçer, böylece kullanıcı
 * hangi pasteli seçerse seçsin arayüz okunabilir kalır.
 */

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** "#8FBD56" veya "8fbd56" veya "#abc" → { r, g, b } */
export function hexToRgb(hex: string): Rgb | null {
  const m = HEX_RE.exec(hex.trim());
  if (!m) return null;

  let body = m[1]!;
  if (body.length === 3) {
    body = body
      .split('')
      .map((c) => c + c)
      .join('');
  }

  return {
    r: parseInt(body.slice(0, 2), 16),
    g: parseInt(body.slice(2, 4), 16),
    b: parseInt(body.slice(4, 6), 16),
  };
}

export function rgbToHex({ r, g, b }: Rgb): string {
  const to2 = (n: number) => Math.round(clamp(n, 0, 255)).toString(16).padStart(2, '0');
  return `#${to2(r)}${to2(g)}${to2(b)}`;
}

/** CSS custom property biçimi: "143, 189, 86" */
export function rgbToCssTriplet({ r, g, b }: Rgb): string {
  return `${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}`;
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

/** Tek bir kanalı sRGB'den doğrusal uzaya çevirir (WCAG 2.x tanımı). */
function linearizeChannel(value8bit: number): number {
  const c = value8bit / 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** Göreli parlaklık (relative luminance), 0 (siyah) – 1 (beyaz). */
export function relativeLuminance({ r, g, b }: Rgb): number {
  return (
    0.2126 * linearizeChannel(r) + 0.7152 * linearizeChannel(g) + 0.0722 * linearizeChannel(b)
  );
}

/** İki renk arasındaki kontrast oranı: 1:1 (aynı) – 21:1 (siyah/beyaz). */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const lighter = Math.max(la, lb);
  const darker = Math.min(la, lb);
  return (lighter + 0.05) / (darker + 0.05);
}

const WHITE: Rgb = { r: 255, g: 255, b: 255 };
const BLACK: Rgb = { r: 0, g: 0, b: 0 };

/** WCAG AA normal metin eşiği. */
export const AA_CONTRAST = 4.5;

/**
 * Verilen rengi, hedef zemine karşı istenen kontrastı sağlayana kadar
 * adım adım koyulaştırır (koyu zeminde ise açar).
 *
 * Renk tonunu (hue) korumak için basit ölçekleme kullanılır: kanallar aynı
 * oranda çarpılır. HSL üzerinden lightness düşürmek de mümkündü, ancak bu
 * yöntem yeşil/turuncu gibi renklerde algılanan tonu daha iyi koruyor.
 */
export function ensureContrast(
  color: Rgb,
  against: Rgb = WHITE,
  target: number = AA_CONTRAST,
): Rgb {
  if (contrastRatio(color, against) >= target) return color;

  // Zemin açıksa koyultuyoruz, koyuysa açıyoruz.
  const backgroundIsLight = relativeLuminance(against) > 0.5;
  const towards = backgroundIsLight ? BLACK : WHITE;

  // 40 adım ~ %2,5'lik hassasiyet; gözle ayırt edilemeyecek kadar ince.
  const STEPS = 40;
  let best = color;

  for (let i = 1; i <= STEPS; i++) {
    const t = i / STEPS;
    const candidate: Rgb = {
      r: color.r + (towards.r - color.r) * t,
      g: color.g + (towards.g - color.g) * t,
      b: color.b + (towards.b - color.b) * t,
    };
    best = candidate;
    if (contrastRatio(candidate, against) >= target) break;
  }

  return best;
}

/**
 * Bir ana renkten tema için gereken iki token'ı üretir.
 *
 * - `primaryRgb`: dolgu, grafik serisi, ikon dairesi (kontrast şartı yok)
 * - `primaryStrongRgb`: link, metin, odak halkası (AA garantili)
 *
 * `isDark` true ise koyu zemine göre hesaplanır; koyu temada "güçlü" varyant
 * daha AÇIK olur, aksi halde okunmaz.
 */
export function derivePrimaryTokens(
  hex: string,
  isDark = false,
): { primaryRgb: string; primaryStrongRgb: string } | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;

  const background: Rgb = isDark ? { r: 34, g: 37, b: 47 } : WHITE;
  const strong = ensureContrast(rgb, background, AA_CONTRAST);

  return {
    primaryRgb: rgbToCssTriplet(rgb),
    primaryStrongRgb: rgbToCssTriplet(strong),
  };
}
